/**
 * Thin API client.
 *
 * The bearer token is held in memory rather than localStorage: this app shows
 * firm-wide financial data, and an XSS-readable token is a materially worse
 * failure than one a script has to go through the server to obtain.
 *
 * Memory does not survive a reload, so the token alone is not the session.
 * The API also issues it in an httpOnly cookie, and `POST /auth/refresh` hands
 * it back on boot — see `lib/session.tsx` and the API's
 * `services/session_cookie.ts` (KAN-104).
 */
let accessToken: string | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

type UnauthorizedHandler = () => void

let onUnauthorized: UnauthorizedHandler | null = null

/**
 * Registers what to do when the server rejects a token we believed was good.
 *
 * Tokens last twelve hours and are revoked outright when an account is
 * deactivated, so this fires in normal use. Without it the SPA sits on a dead
 * session showing errors on every panel instead of returning the user to the
 * sign-in screen.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Machine-readable marker, when the endpoint sends one. */
    readonly code: string | null = null
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api'

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  const authenticated = accessToken !== null
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }

  /**
   * Credentials are sent on every request rather than only on the three auth
   * routes that need the session cookie. `BASE_URL` may point at another
   * origin, where fetch's `same-origin` default would silently drop the cookie
   * — and a sign-in that appears to work but never persists is a poor way to
   * find that out. The API reads the cookie on `/auth/refresh` alone, so
   * carrying it elsewhere grants nothing.
   */
  const response = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers,
  })

  if (!response.ok) {
    const { message, code } = await readError(response)

    /**
     * Only when we actually sent a token. A 401 from the login endpoint means
     * the credentials were wrong, not that a session expired, and tearing down
     * a session that does not exist would just clear the form.
     */
    if (response.status === 401 && authenticated) {
      onUnauthorized?.()
    }

    throw new ApiError(response.status, message, code)
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T)
}

/**
 * Errors from this API are `{ error, code? }`. Anything else — a proxy error
 * page, a crash before the handler ran — is reported by status text rather
 * than by dumping HTML into the interface.
 */
async function readError(
  response: Response
): Promise<{ message: string; code: string | null }> {
  try {
    const body = (await response.json()) as { error?: string; code?: string }
    return {
      message: typeof body.error === 'string' ? body.error : response.statusText,
      code: typeof body.code === 'string' ? body.code : null,
    }
  } catch {
    return { message: response.statusText, code: null }
  }
}

/**
 * Downloads a file from an authenticated endpoint.
 *
 * A plain `<a href>` cannot be used, and the reason is the token design: the
 * bearer token is held in memory rather than in a cookie or localStorage, so a
 * browser navigation carries no credential and the export would 401. So the
 * request is made by `fetch` with the header attached, and the response is
 * turned into an object URL and clicked.
 *
 * The object URL is revoked immediately after. A blob left un-revoked pins its
 * bytes in memory for the life of the document, and a spreadsheet of every
 * receivable is not small.
 */
export async function apiDownload(
  path: string,
  filename: string,
  accept = 'text/csv'
): Promise<void> {
  const headers = new Headers({ Accept: accept })
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)

  const response = await fetch(`${BASE_URL}${path}`, { credentials: 'include', headers })

  if (!response.ok) {
    const { message, code } = await readError(response)
    throw new ApiError(response.status, message, code)
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)

  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()

  URL.revokeObjectURL(url)
}
